import { useAppTranslation } from '@hooks';
import { memo, useState } from 'react';
import { css } from '@emotion/react';
import { LoadingOutlined } from '@ant-design/icons';
import { AssignSegment, AssignSegmentPropsType } from '@views/shared/segment';
import { ActionModal, Row, colors, Text } from 'common-components';
import { UserType } from '@definitions/states';
import { findKeyByValue } from '@utils/find-key';

import { FormUser, FormUserDataType } from '@views/shared/user';

type CreateContributorModalComponentPropsType = Pick<
  AssignSegmentPropsType,
  'onAddSegment' | 'onDeleteSegment' | 'currentManager'
> & {
  onCreateContributor(contributorForm: FormUserDataType, onStepChange: () => void): Promise<void>
  onInvitationSent: (name: string) => void
  close: () => void
  isOpen: boolean
  isLoading: boolean
};

const totalSteps = 2;

const CreateContributorModalComponent = ({
  onCreateContributor,
  onInvitationSent,
  onAddSegment,
  onDeleteSegment,
  close,
  isOpen,
  isLoading,
  currentManager,
}: CreateContributorModalComponentPropsType) => {
  const [contributorForm, setContributorForm] = useState<FormUserDataType>(null);

  const { translate } = useAppTranslation();
  const [step, setStep] = useState(1);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isCandidateInvited, setIsCandidateInvited] = useState<boolean>(false);
  const [invitedName, setInvitedName] = useState<string>('');

  const getNextButtonLabel = () => {
    if (step === 1) {
      return translate('createSegment.next');
    }
    return isCandidateInvited
      ? translate('contributors.inviteCandidate')
      : translate('contributors.inviteContributor');
  };

  return (
    <ActionModal
      key={
        [
          translate('contributors.addContributor'),
          translate('common.chooseSegments'),
          translate('common.chooseCertifications'),
        ][step - 1]
      }
      mask={{ closable: false }}
      width={660}
      open={isOpen}
      onOk={close}
      customHeader={
        <Row
          css={css`
            flex-direction: column;
            gap: 5px;
          `}
        >
          <Text color={colors.gray400} size="md" style={{ fontWeight: 500 }}>
            {step} {translate('createSegment.outOf')} {totalSteps}
          </Text>
          <Text
            css={css`
              font-weight: 500;
            `}
            color={colors.gray900}
            size="lg"
          >
            {step === 1 && translate('contributors.addContributor')}
            {step === 2 && translate('common.chooseSegments')}
          </Text>
        </Row>
      }
      onConfirm={close}
      title={
        [
          translate('contributors.addContributor'),
          translate('common.chooseSegments'),
          translate('common.chooseCertifications'),
        ][step - 1]
      }
      onCancel={() => {
        setStep && setStep(1);
        close();
      }}
      actionButtons={[
        {
          text: translate('managers.cancel'),
          onClick: () => {
            setStep && setStep(1);
            close();
          },
        },
        {
          text: isLoading ? <LoadingOutlined /> : getNextButtonLabel(),
          type: 'primary',
          disabled: !isFormValid,
          onClick: async () => {
            if (setStep) {
              //////// STEP 1 ////////
              if (step === 1) {
                if (isFormValid && contributorForm?.createUser) {
                  setIsCandidateInvited(
                    contributorForm.createUser.type === findKeyByValue(UserType, 'candidate'),
                  );
                  setInvitedName(
                    `${contributorForm.createUser.firstName} ${contributorForm.createUser.lastName}`,
                  );
                  await onCreateContributor(contributorForm, () => {
                    setStep(step + 1);
                    setContributorForm(null);
                  });
                }
              }
              //////// STEP 2 ////////
              if (step === 2) {
                onInvitationSent(invitedName);
                close();
                setStep(step + 1);
                return;
              }
            } else {
              close();
            }
          },
        },
      ]}
    >
      <>
        {step === 1 && (
          <FormUser
            roles={['worker', 'candidate']}
            isFormValid={(validation) => setIsFormValid(validation)}
            onChangeForm={({ createUser, file }) => {
              setContributorForm({ createUser, file });
            }}
          />
        )}
        {step === 2 && (
          <AssignSegment
            onAddSegment={onAddSegment}
            onDeleteSegment={onDeleteSegment}
            currentManager={currentManager}
          />
        )}
      </>
    </ActionModal>
  );
};

const CreateContributorModal = memo(
  CreateContributorModalComponent,
  (
    oldProps: CreateContributorModalComponentPropsType,
    newProps: CreateContributorModalComponentPropsType,
  ) => {
    return JSON.stringify(oldProps) === JSON.stringify(newProps);
  },
);

export default CreateContributorModal;
